import { type FC, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { PrivateAccess } from '@wf/keycloak-axios-provider';
import { Button, ClipLoader, Icon } from '@wf/components';
import { Tooltip } from '@wf/sw-ui-kit';

import LicenseStatus from '../../../../components/LicenseStatus';
import { useLicenseContext } from '../../LicenseModeContext';
import ImportExportLicense from '../ImportExportLicense';
import RiskStatus from '../RiskStatus';
import FeePopup from '../FeePopup';

import { declarantAndTraderRoles, officerAndAdminRoles, viewRoles } from '../../../../constants';
import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import useDownloadLicense from '../../hooks/useDownloadLicense';
import { Status } from '../../types/enums';

import {
  approvalReferenceSelector,
  documentStatusesSelector,
  downloadPermissionSelector,
  featureFlagsSelector,
  isChildLpcoSelector,
  parentLpcoSelector,
  subLicenseTypesSelector,
} from '../../store/selectors';
import { COUNTRY } from '../../../../config';

import styles from './DocumentInfo.module.scss';

interface IDocumentInfo {
  id: string;
}

const DocumentInfo: FC<IDocumentInfo> = ({ id }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const { handleDownloadLicense, isDownloading } = useDownloadLicense(id);
  const { handleDownloadLicense: handleDownloadLicensePreview, isDownloading: isDownloadingPreview } =
    useDownloadLicense(id, true);
  const licenseAbbreviation = useLicenseAbbreviation();
  const { createMode } = useLicenseContext();

  const { status, ogaStatus, ogaStatusInNationalLang } = useSelector(documentStatusesSelector);
  const showPrintButton = useSelector(downloadPermissionSelector);
  const featureFlags = useSelector(featureFlagsSelector);
  const isChildLpco = useSelector(isChildLpcoSelector);
  const approvalReference = useSelector(approvalReferenceSelector);
  const { data: parentLpco } = useSelector(parentLpcoSelector);
  const subLicenseTypeCodes = useSelector(subLicenseTypesSelector);
  const isParentLpco = !!subLicenseTypeCodes?.length;

  const [showFeePopup, setShowFeePopup] = useState(false);

  const showRiskStatus =
    status &&
    status !== Status.STORED &&
    (featureFlags?.riskExecutionForSubmitEnabled || featureFlags?.riskExecutionForApproveEnabled);
  const showAmountToBePaidButton =
    featureFlags?.showAmountToBePaidEnabled &&
    (status === Status.REQUESTED || status === Status.QUERIED || (status && !(status in Status)));

  return (
    <div className={styles.container}>
      <div className={styles.info}>
        <div className={styles.status}>
          <h3 className="fs-12 fw-semibold">{t('Document Status')}:</h3>
          {status && (
            <LicenseStatus
              ogaStatusInNationalLang={ogaStatusInNationalLang}
              ogaStatus={ogaStatus}
              status={status}
              size="sm"
            />
          )}
        </div>
        {showRiskStatus && (
          <PrivateAccess roles={officerAndAdminRoles}>
            <RiskStatus />
          </PrivateAccess>
        )}
      </div>
      <div className={styles.actions}>
        {showAmountToBePaidButton && (
          <Button onClick={() => setShowFeePopup(true)} size="sm" linkButton>
            {t('Amount To Be Paid')}
          </Button>
        )}

        {!isChildLpco && (
          <PrivateAccess roles={declarantAndTraderRoles}>
            <ImportExportLicense />
          </PrivateAccess>
        )}

        {showPrintButton && (
          <>
            <Button
              loadingIcon={<ClipLoader className={styles.loader} />}
              leftIcon={<Icon name="ic_print" />}
              onClick={handleDownloadLicense}
              isLoading={isDownloading}
              size="sm"
              linkButton
            >
              {COUNTRY === 'bangladesh' ? t('Print CLP') : t('Print PDF')}
            </Button>
          </>
        )}
        {featureFlags?.printPreviewEnabled && !createMode && (
          <Button
            loadingIcon={<ClipLoader className={styles.loader} />}
            onClick={handleDownloadLicensePreview}
            leftIcon={<Icon name="ic_print" />}
            isLoading={isDownloadingPreview}
            size="sm"
            linkButton
          >
            {t('Print Preview')}
          </Button>
        )}

        {!createMode && (
          <PrivateAccess roles={viewRoles}>
            {isParentLpco && approvalReference && (
              <Link to={`../../${id}/children`} relative="path">
                <Button leftIcon={<Icon name="ic_list" />} size="sm" linkButton>
                  {t('Child {{licenseAbbreviation}}', { licenseAbbreviation })}
                </Button>
              </Link>
            )}
            {parentLpco?.id && (
              <Tooltip
                tooltipContent={
                  <span>
                    {t('Reference Number')}: <span className={styles.bold}>{parentLpco.userReferenceNumber}</span>
                    <br />
                    {t('Type')}: <span className={styles.bold}>{parentLpco.licenseType}</span>
                  </span>
                }
                placement="bottom"
                offset={0}
              >
                <Link to={`../../view/${parentLpco.id}`} target="_blank" relative="path">
                  <Button leftIcon={<Icon name="ic_list" />} size="sm" linkButton>
                    {t('Parent {{licenseAbbreviation}}', { licenseAbbreviation })}
                  </Button>
                </Link>
              </Tooltip>
            )}
            <Link to={`../../documentHistory/${id}`} state={{ from: location }} relative="path">
              <Button
                title={t(
                  'Displays a chronological record of all operations and status changes of the {{licenseAbbreviation}} application',
                  { licenseAbbreviation }
                )}
                leftIcon={<Icon name="ic_time" />}
                size="sm"
                linkButton
              >
                {t('Document History')}
              </Button>
            </Link>
          </PrivateAccess>
        )}
      </div>
      {showFeePopup && <FeePopup onClose={() => setShowFeePopup(false)} />}
    </div>
  );
};

export default DocumentInfo;
